import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Share2 } from "../assets/icons/share2.svg";
import { ReactComponent as FacebookIcon } from "../assets/icons/facebook.svg";
import { ReactComponent as LinkedInIcon } from "../assets/icons/linkedin.svg";
import { ReactComponent as PinterestIcon } from "../assets/icons/pinterest.svg";
import { ReactComponent as XIcon } from "../assets/icons/x.svg";
import { ReactComponent as WhatsAppIcon } from "../assets/icons/whatsapp.svg";
import { ReactComponent as TelegramIcon } from "../assets/icons/telegram.svg";
import QRCode from "qrcode";

const ShareButtons = ({ post = {}, lang = "en", shareUrlOverride, kind = "blog" }) => {
  const { t } = useTranslation();
  const baseUrl = "https://modularobjects.com";

  const {
    title = t("share.defaults.title"),
    image = "",
    tags = [],
    description = "",
    slug = "ar-3d-interior-planning",
  } = post;

  // ✅ normalizuj jezik
  const lang2 = String(lang || "en").toLowerCase().startsWith("sr") ? "sr" : "en";

  // ✅ KANONSKI share URL (bez UTM) koji platforme scrape-uju
 const shareUrl =
  shareUrlOverride ||
  (kind === "project"
    ? `${baseUrl}/share/projects/${lang2}/${encodeURIComponent(slug)}.html`
    : kind === "blog"
    ? `${baseUrl}/share/${encodeURIComponent(slug)}.html`
    : kind === "page"
    ? `${baseUrl}/share/${lang2}/${encodeURIComponent(slug)}.html`
    : `${baseUrl}/share/${lang2}/${encodeURIComponent(slug)}.html`);

  const campaign = kind === "project" ? "projects" : "blog";

  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrForUrl, setQrForUrl] = useState("");
  const [toast, setToast] = useState("");

  // ✅ apsolutna slika za Pinterest (nije presudno za FB/WA, ali pomaže)
  const fullImageUrl = image?.startsWith("http")
    ? image
    : `${baseUrl}${image.startsWith("/") ? "" : "/"}${image}`;

  const encodedTitle = encodeURIComponent(description || title);
  const encodedImage = encodeURIComponent(fullImageUrl);
  const iconClass = "w-6 h-6 transition hover:opacity-80";

  const twitterTags =
    tags.length > 0
      ? tags.map((tag) => tag.replace(/\s+/g, "")).join("%20")
      : "design%20interior";

  // ✅ UTM samo za tracking/copy (ne za scrape)
  const withUtm = (url, source) => {
    const u = new URL(url);
    u.searchParams.set("utm_source", source);
    u.searchParams.set("utm_medium", "share");
    u.searchParams.set("utm_campaign", campaign);
    return u.toString();
  };

  const trackShare = (source) => {
    window.dataLayer?.push({
      event: "share_click",
      share_source: source,
      share_campaign: campaign,
      share_url_clean: shareUrl,
      share_url: withUtm(shareUrl, source),
    });
  };

  // ✅ copy može UTM, platform share NE
  const copyUrl = withUtm(shareUrl, "copy");

  // ✅ encoded clean URL
  const cleanEncoded = encodeURIComponent(shareUrl);

// ✅ DEBUG: vidi šta tačno generišemo za share
if (typeof window !== "undefined") {
  window.__share_debug__ = {
    kind,
    lang2,
    slug,
    shareUrl,
    copyUrl,
    fullImageUrl,
    campaign,
    title,
    description,
    tags,
  };
  // samo ako hoćeš da uvek ispisuje:
  // console.log("SHARE DEBUG", window.__share_debug__);
}

  return (
    <div className="mt-8">
      {/* Header: label + copy */}
      <div className="flex items-center gap-2 w-full mb-4">
        <Share2 className="w-5 h-5 text-primary-light" />
        <span className="text-sm font-medium text-text-light dark:text-text-dark">
          {t("share.label")}
        </span>

        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(copyUrl);
            trackShare("copy");
            setToast(t("share.toast.copied"));
            setTimeout(() => setToast(""), 2000);
          }}
          className="text-sm underline text-primary-light dark:text-primary-dark hover:opacity-80"
        >
          {t("share.copyLink")}
        </button>
      </div>

      {/* Icons */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Facebook */}
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${cleanEncoded}&quote=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.facebook")}
          onClick={() => trackShare("facebook")}
          className="hover:opacity-75 transition-opacity"
        >
          <FacebookIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* LinkedIn */}
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${cleanEncoded}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.linkedin")}
          onClick={() => trackShare("linkedin")}
          className="hover:opacity-75 transition-opacity"
        >
          <LinkedInIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* Pinterest */}
        <a
          href={`https://pinterest.com/pin/create/button/?url=${cleanEncoded}&media=${encodedImage}&description=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.pinterest")}
          onClick={() => trackShare("pinterest")}
          className="hover:opacity-75 transition-opacity"
        >
          <PinterestIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* X */}
        <a
          href={`https://twitter.com/intent/tweet?url=${cleanEncoded}&text=${encodedTitle}&hashtags=${twitterTags}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.x")}
          onClick={() => trackShare("x")}
          className="hover:opacity-75 transition-opacity"
        >
          <XIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* WhatsApp */}
        <a
          href={`https://wa.me/?text=${encodeURIComponent(title + "\n" + shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.whatsapp")}
          onClick={() => trackShare("whatsapp")}
          className="hover:opacity-75 transition-opacity"
        >
          <WhatsAppIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* Telegram */}
        <a
          href={`https://t.me/share/url?url=${cleanEncoded}&text=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t("share.aria.telegram")}
          onClick={() => trackShare("telegram")}
          className="hover:opacity-75 transition-opacity"
        >
          <TelegramIcon className={`${iconClass} fill-primary-light`} />
        </a>

        {/* QR toggle */}
        <button
          type="button"
          onClick={async () => {
            const next = !showQr;
            setShowQr(next);

            if (next) trackShare("qr");

            // ✅ QR vodi na clean share URL (stabilno)
            const qrTargetUrl = shareUrl;

            if (next && (!qrDataUrl || qrForUrl !== qrTargetUrl)) {
              try {
                const dataUrl = await QRCode.toDataURL(qrTargetUrl, {
                  width: 180,
                  margin: 1,
                });
                setQrDataUrl(dataUrl);
                setQrForUrl(qrTargetUrl);
              } catch (e) {
                console.error("QR generate error:", e);
                alert(t("share.qr.error"));
              }
            }
          }}
          className="text-xs px-3 py-2 rounded border border-gray-200 dark:border-gray-800 hover:opacity-90 bg-white/60 dark:bg-black/20"
        >
          {showQr ? t("share.qr.hide") : t("share.qr.show")}
        </button>
      </div>

      {/* QR */}
      {showQr && (
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={t("share.qr.alt")}
              className="w-40 h-40 rounded border border-gray-200 dark:border-gray-800 bg-white p-2"
              loading="lazy"
            />
          ) : (
            <div className="w-40 h-40 rounded border border-gray-200 dark:border-gray-800 bg-white p-2 flex items-center justify-center text-sm">
              {t("share.qr.generating")}
            </div>
          )}

          <div className="text-sm text-text-light/80 dark:text-text-dark/80">
            {t("share.qr.hint")}
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div
          className="
            fixed bottom-6 left-1/2 -translate-x-1/2
            bg-black/80 text-white text-sm
            px-4 py-2 rounded-lg shadow-lg
            z-50
          "
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default ShareButtons;